import React from "react";
import { usePageMeta } from "../lib/usePageMeta";
import { Link } from "react-router-dom";
import { Hexagon, ShieldCheck, Certificate, ArrowRight, MapPin, ClipboardText, Headset } from "@phosphor-icons/react";

/**
 * About page — plain-English rewrite (2026-08).
 *
 * Keeps every truthful fact intact:
 *   * U.S.-based independent supplier of research peptides.
 *   * 106 SKUs, batch-tracked inventory, live shipping rates, room-
 *     temperature shipping for freeze-dried vials, signature at delivery.
 *   * COA = Certificate of Analysis — explained ONCE, plain-English after.
 *   * Research-use-only status is stated up front.
 */
export default function About() {
  usePageMeta({
    title: "About · U.S.-based research supply",
    description:
      "Medical Peptide Store is a U.S.-based supplier of research peptides. Clear batch documents, live shipping rates, room-temperature shipping, and signature delivery. For research use only.",
    canonicalPath: "/about",
  });

  return (
    <div className="bg-white" data-testid="about-page">
      {/* Intro band */}
      <section className="bg-brand-surface border-b border-brand-border">
        <div className="mps-container py-16 md:py-20 max-w-4xl">
          <div className="eyebrow mb-4">About</div>
          <h1
            data-testid="about-h1"
            className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-brand-navy leading-tight"
          >
            A clear, honest catalog for research peptides.
          </h1>
          <p className="mt-6 text-brand-muted text-lg leading-relaxed max-w-2xl">
            Medical Peptide Store is a U.S.-based supplier. We sell research
            peptides to qualified laboratories and researchers. Every
            product is a research reference standard —{" "}
            <strong className="text-brand-navy">for research use only</strong>,
            not for human or animal use.
          </p>
        </div>
      </section>

      {/* What we do — three short blocks */}
      <section className="mps-container py-14 md:py-20 max-w-5xl">
        <div className="max-w-3xl mb-10">
          <div className="eyebrow mb-3">What we do</div>
          <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-brand-navy leading-tight">
            Three things we take seriously.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
          {[
            {
              icon: Hexagon,
              k: "Batch-tracked catalog",
              v: "Every listing is tied to a specific batch. The paperwork you receive lines up with the vial inside the box.",
              tid: "about-block-batch",
            },
            {
              icon: ShieldCheck,
              k: "Compliance-first",
              v: "Products shown as in stock can be purchased with secure guest checkout. Some products or destinations may be restricted; we say so before you pay.",
              tid: "about-block-compliance",
            },
            {
              icon: Certificate,
              k: "Honest documents",
              v: "We publish a batch document (a Certificate of Analysis, or COA) once it has been reviewed. We never show generic or placeholder documents.",
              tid: "about-block-docs",
            },
          ].map(({ icon: Icon, k, v, tid }) => (
            <div key={k} data-testid={tid} className="mps-card p-7">
              <div className="h-10 w-10 rounded-[10px] bg-brand-surface border border-brand-border flex items-center justify-center mb-4">
                <Icon size={18} weight="regular" className="text-brand-navy" aria-hidden />
              </div>
              <div className="font-display font-semibold text-brand-navy text-lg tracking-tight">
                {k}
              </div>
              <p className="mt-2 text-sm text-brand-muted leading-relaxed">
                {v}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Operating facts */}
      <section className="bg-brand-navy text-white">
        <div className="mps-container py-14 md:py-20 max-w-5xl">
          <div className="max-w-3xl mb-10">
            <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-accent mb-3">
              How we operate
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight !text-white leading-tight">
              The basics, in plain English.
            </h2>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-5 text-[15px] leading-relaxed">
            {[
              { icon: MapPin,        k: "Where we ship from",   v: "United States. Every order ships tracked with a signature required at delivery." },
              { icon: ClipboardText, k: "Inventory",            v: "Around 106 active research peptides across nine research areas. Prices are checked securely before payment." },
              { icon: ShieldCheck,   k: "Shipping",             v: "Freeze-dried research vials ship at room temperature in tamper-evident boxes. Live carrier rates are quoted at checkout." },
              { icon: Headset,       k: "Support",              v: "A real person answers info@medicalpeptidestore.com during our published support hours." },
            ].map(({ icon: Icon, k, v }) => (
              <li
                key={k}
                data-testid={`about-fact-${k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="rounded-[14px] bg-white/5 border border-white/10 backdrop-blur-sm p-6 flex items-start gap-4"
              >
                <Icon size={20} weight="regular" className="text-brand-accent shrink-0 mt-0.5" aria-hidden />
                <div>
                  <div className="font-semibold text-white">{k}</div>
                  <div className="mt-1 text-white/80">{v}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="mps-container py-14 md:py-20 max-w-4xl text-center">
        <h3 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-brand-navy leading-tight">
          Ready to browse?
        </h3>
        <p className="mt-3 text-brand-muted max-w-lg mx-auto">
          Explore the full catalog and check out securely.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
          <Link
            to="/shop"
            data-testid="about-shop-cta"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-[12px] bg-brand-navy hover:bg-brand-navy2 text-white font-semibold text-sm transition-colors"
          >
            Browse catalog <ArrowRight size={14} weight="bold" />
          </Link>
          <Link
            to="/contact"
            data-testid="about-contact-cta"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-[12px] border border-brand-border text-brand-navy hover:bg-brand-surface font-semibold text-sm transition-colors"
          >
            Contact us
          </Link>
        </div>
      </section>
    </div>
  );
}
